<template>
  <view-wrapper class="space-y-6">
    <page-title
      data-cy="command-bar-settings-title"
      :title="$t('PROFILE.COMMAND_BAR')"
    />
    <div>
      <toggle-section
        section="aiAgentsSettings"
        chevron-position="right"
        :has-header-bottom-border="false"
        :has-padding="false"
        :is-open="isOpen"
        class="w-full bg-white shadow-xs 2xl:w-1/2 lg:w-3/4 xl:w-[63%]"
        @toggle="isOpen = !isOpen"
      >
        <template #titleSection>
          <div class="flex flex-col">
            <lf-h4>{{ $t("AI_AGENTS.TITLE") }}</lf-h4>
            <p class="text-headline">
              {{
                $t("AI_AGENTS.SETTINGS.SUBTITLE", {
                  orgText: isAdmin
                    ? $t("AI_AGENTS.SETTINGS.SUBTITLE_ORG_SETTINGS_PART")
                    : "",
                })
              }}
            </p>
          </div>
        </template>
        <template #content>
          <div class="space-y-6 border-t border-divider bg-grey-hover p-6">
            <search-input
              v-model="searchTerm"
              no-padding
              :placeholder="searchPlaceholder"
              class="max-w-60"
            />
            <div class="space-y-6">
              <div class="grid grid-cols-4 text-label">
                <div class="col-span-2 text-label">{{ $t("COMMON.NAME") }}</div>
                <div v-if="isAdmin" class="text-label">
                  {{ $t("COMMON.ORGANIZATION") }}
                </div>
                <div v-if="isAdmin" class="text-label">
                  {{ $t("PROFILE.ONLY_FOR_ME") }}
                </div>
              </div>
              <div
                ref="container"
                class="max-h-[calc(100vh-350px)] space-y-8 overflow-y-auto"
              >
                <loader :is-loading="isLoading" background-color="#F9F9FB" />
                <div
                  v-for="agent in agentSettings"
                  :key="agent.id"
                  class="grid grid-cols-4"
                >
                  <div
                    class="text-label"
                    :class="isAdmin ? 'col-span-2' : 'col-span-3'"
                  >
                    {{ agent.name }}
                  </div>
                  <lf-switch
                    v-if="isAdmin"
                    v-model="agent.enabledForClient"
                    :name="`${agent.name}-client`"
                    @update:model-value="
                      handleAgentVisibilityChange(
                        agent.id,
                        AgentVisibilityType.Client,
                        $event
                      )
                    "
                  />
                  <lf-switch
                    v-model="agent.enabledForUser"
                    :class="{ 'ml-auto': !isAdmin }"
                    :name="`${agent.name}-user`"
                    @update:model-value="
                      handleAgentVisibilityChange(
                        agent.id,
                        AgentVisibilityType.User,
                        $event
                      )
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </toggle-section>
    </div>
  </view-wrapper>
</template>

<script setup lang="ts">
import { useInfiniteScroll, watchOnce } from "@vueuse/core";
import { computed, onBeforeMount, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { AgentVisibilityType } from "@/enums/ai";
import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import {
  useAgentVisibilityMutation,
  useAgentsInfiniteQuery,
} from "@/hooks/queries/useAgents";
import router from "@/router";
import { ROUTE_HOME } from "@/router/routes";
import { useAnalyticsStore } from "@/stores/analytics";

import ToggleSection from "@/components/ToggleSection.vue";
import Loader from "@/components/ui/Loader.vue";
import PageTitle from "@/components/ui/PageTitle.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import ViewWrapper from "@/views/ViewWrapper.vue";

interface AIAgentSetting {
  name: string;
  id: string;
  enabledForClient: boolean;
  enabledForUser: boolean;
}

const { t } = useI18n();
const searchPlaceholder = `${t("COMMON.SEARCH")} ${t("AI_AGENTS.TITLE")}`;

const { isAdmin } = useAuth();
const { checkPermissions } = usePermissions();
const { showMessage } = useNotification();
const { trackEvent } = useAnalyticsStore();

const agentVisibilityMutation = useAgentVisibilityMutation();

const isOpen = ref(false);
const searchTerm = ref("");

const container = useTemplateRef("container");

const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
  useAgentsInfiniteQuery({
    search: searchTerm,
    include_visibility_settings: 1,
  });

const agentSettings = computed<AIAgentSetting[]>(() => {
  const agents = data.value?.pages.flatMap((i) => i.data);

  return (
    agents?.reduce((result: AIAgentSetting[], { name, id, visibilities }) => {
      const { clientVisibility, userVisibility } =
        visibilities?.reduce(
          (
            acc: {
              clientVisibility?: (typeof visibilities)[number];
              userVisibility?: (typeof visibilities)[number];
            },
            visibility
          ) => {
            if (visibility.applies_to_type === AgentVisibilityType.Client) {
              acc.clientVisibility = visibility;
            }
            if (visibility.applies_to_type === AgentVisibilityType.User) {
              acc.userVisibility = visibility;
            }
            return acc;
          },
          {}
        ) ?? {};

      /**
       * if a visibility is not in the array,
       * by default we assume it is true because this is the default value
       */

      const newSetting = {
        name,
        id,
        enabledForClient: clientVisibility ? clientVisibility.is_visible : true,
        enabledForUser: userVisibility ? userVisibility.is_visible : true,
      };

      result.push(newSetting);
      return result;
    }, []) || []
  );
});

const { fetchWrapper: checkPermission } = usePromiseWrapper(async () => {
  const canViewAIAgents = await checkPermissions(
    PermissionSubject.aiAgent,
    Ability.viewAny,
    {}
  );
  if (!canViewAIAgents) {
    showMessage(t("COMMON.ACCESS_RESTRICTED"), "error");
    router.push({ name: ROUTE_HOME });
    return;
  }
});

const handleAgentVisibilityChange = async (
  id: string,
  role: AgentVisibilityType,
  value: boolean
) => {
  const updatedAgentSettings = agentSettings.value.find(
    (agent) => agent.id === id
  );

  if (!updatedAgentSettings) {
    return;
  }

  const payload = {
    ai_agents: [
      {
        id,
        is_visible: value,
      },
    ],
  };

  await agentVisibilityMutation.mutateAsync({
    payload,
    role,
    search: searchTerm,
  });

  trackEvent(AnalyticsEvent.CmdKVisibilityChanged, {
    agent_name: updatedAgentSettings.name,
    role,
    value,
  });
};

useInfiniteScroll(
  container,
  () => {
    if (!isLoading.value && !isFetchingNextPage.value) {
      fetchNextPage();
    }
  },
  {
    distance: 20,
    canLoadMore: () => hasNextPage.value,
  }
);

watchOnce(agentSettings, () => {
  isOpen.value = agentSettings.value.some(
    ({ enabledForClient, enabledForUser }) => enabledForClient || enabledForUser
  );
});

onBeforeMount(checkPermission);
</script>
